import React, { ReactNode } from 'react';
import { ArrowDropDown } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { ActionsMenu } from 'components/shared/Menu/ActionsMenu';

import { IndicatorDot } from './IndicatorDot';

export interface IndicatorMenuOption<T extends string> {
  value: T;
  label: string;
  color: string;
  disabled?: boolean;
  renderIcon?: (color: string) => ReactNode;
}

interface Props<T extends string> {
  value: T | null;
  options: IndicatorMenuOption<T>[];
  onChange: (next: T) => void;
  /** Shown when no option is selected yet */
  placeholder?: string;
}

const renderOptionIcon = <T extends string>(option: IndicatorMenuOption<T>) =>
  option.renderIcon ? option.renderIcon(option.color) : <IndicatorDot color={option.color} />;

export const IndicatorMenu = <T extends string>({ value, options, onChange, placeholder = '' }: Props<T>) => {
  const current = options.find(option => option.value === value);

  return (
    <Stack direction="row" alignItems="center" gap="0.5rem">
      {current ? renderOptionIcon(current) : <IndicatorDot color="transparent" />}

      <Typography variant="body1" sx={{ color: current?.color ?? 'text.secondary' }}>
        {current?.label ?? placeholder}
      </Typography>

      <ActionsMenu
        renderTrigger={({ onClick }) => (
          <IconButton onClick={onClick} size="small">
            <ArrowDropDown sx={{ color: current?.color ?? 'black' }} fontSize="small" />
          </IconButton>
        )}
        config={options.map(option => ({
          label: option.label,
          color: option.color,
          disabled: option.disabled,
          action: () => onChange(option.value),
          icon: renderOptionIcon(option),
        }))}
      />
    </Stack>
  );
};
